import React, { useState, useEffect } from "react";
import { X, Save, Plus, Check, ChevronRight } from "lucide-react";
import axios from "axios";

const EquipmentTypeModal = ({
  isOpen,
  onClose,
  mode = 'create', // 'create', 'edit'
  selectedType = null,
  onSave
}) => {
  const [activeMode, setActiveMode] = useState('global'); // For create mode always 'global', for edit mode 'create'
  const [formData, setFormData] = useState({
    name: '',
    comment: ''
  });
  const [globalTypes, setGlobalTypes] = useState([]);
  const [selectedGlobalTypes, setSelectedGlobalTypes] = useState(new Set());
  const [copyWithModels, setCopyWithModels] = useState(false);
  const [selectedTypeForModels, setSelectedTypeForModels] = useState(null); // Type ID for which models panel is open
  const [modelSearch, setModelSearch] = useState(''); // Search term for models
  const [selectedModels, setSelectedModels] = useState(new Map()); // Map<typeId, Set<modelId>>
  const [typeModels, setTypeModels] = useState(new Map()); // Map<typeId, models[]>
  const [loadingModels, setLoadingModels] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingGlobal, setFetchingGlobal] = useState(false);

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy'}/api`;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && selectedType) {
        setFormData({
          name: selectedType.name || '',
          comment: selectedType.comment || ''
        });
        setActiveMode('create'); // Edit mode uses create activeMode for form display
      } else {
        setFormData({
          name: '',
          comment: ''
        });
        setActiveMode('global'); // Create mode always uses global selection
        // Auto-load global types for create mode
        if (globalTypes.length === 0) {
          fetchGlobalTypes();
        }
      }
      setSelectedGlobalTypes(new Set());
      setCopyWithModels(false);
      setSelectedTypeForModels(null);
      setModelSearch('');
      setSelectedModels(new Map());
      setTypeModels(new Map());
      setLoadingModels(new Set());
      setError(null);
    }
  }, [isOpen, mode, selectedType]);

  // Fetch global types when switching to global mode
  const fetchGlobalTypes = async () => {
    setFetchingGlobal(true);
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.get(`${API_BASE_URL}/safetyequipmenttype/?scope=global&include_model_count=true`, config);
      setGlobalTypes(response.data);
    } catch (err) {
      console.error('Error fetching global types:', err);
      setError('Failed to load global types. Please try again.');
    } finally {
      setFetchingGlobal(false);
    }
  };

  // Note: handleModeSwitch removed - no longer needed as create mode only allows global selection

  // Handle input change for create mode
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch models for a specific type
  const fetchModelsForType = async (typeId) => {
    if (typeModels.has(typeId) || loadingModels.has(typeId)) {
      return; // Already loaded or loading
    }

    setLoadingModels(prev => new Set(prev).add(typeId));

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.get(`${API_BASE_URL}/safetyequipmentmodel/?scope=global&type=${typeId}`, config);

      setTypeModels(prev => new Map(prev).set(typeId, response.data));
      setSelectedModels(prev => new Map(prev).set(typeId, new Set()));
    } catch (err) {
      console.error('Error fetching models for type:', err);
      setError(`Failed to load models for selected type. Please try again.`);
    } finally {
      setLoadingModels(prev => {
        const newSet = new Set(prev);
        newSet.delete(typeId);
        return newSet;
      });
    }
  };

  // Handle global type selection
  const handleGlobalTypeToggle = (typeId) => {
    setSelectedGlobalTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeId)) {
        newSet.delete(typeId);
        // Clear selected models for this type
        setSelectedModels(prevModels => {
          const newMap = new Map(prevModels);
          newMap.delete(typeId);
          return newMap;
        });
        // Close models panel if this type was selected
        if (selectedTypeForModels === typeId) {
          setSelectedTypeForModels(null);
        }
      } else {
        newSet.add(typeId);
      }
      return newSet;
    });
  };

  // Handle model selection within a type
  const handleModelToggle = (typeId, modelId) => {
    setSelectedModels(prev => {
      const newMap = new Map(prev);
      const typeModels = newMap.get(typeId) || new Set();
      const newTypeModels = new Set(typeModels);

      if (newTypeModels.has(modelId)) {
        newTypeModels.delete(modelId);
      } else {
        newTypeModels.add(modelId);
      }

      newMap.set(typeId, newTypeModels);
      return newMap;
    });
  };

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      if (mode === 'edit' && selectedType) {
        // Edit existing type - only comment can be changed
        const typeData = {
          comment: formData.comment.trim()
        };

        await axios.put(`${API_BASE_URL}/safetyequipmenttype/${selectedType.id}/`, typeData, config);
      } else {
        // Copy global types for create mode
        if (selectedGlobalTypes.size === 0) {
          setError('Please select at least one type to add');
          setLoading(false);
          return;
        }

      if (copyWithModels) {
          // Check if any models are specifically selected
          const hasSelectedModels = selectedModels.size > 0 &&
            Array.from(selectedModels.values()).some(modelSet => modelSet.size > 0);

          if (hasSelectedModels) {
            // Validate that at least one model is selected
            const totalSelectedModels = Array.from(selectedModels.values()).reduce(
              (sum, modelSet) => sum + modelSet.size, 0
            );

            if (totalSelectedModels === 0) {
              setError('Please select at least one model to copy.');
              setLoading(false);
              return;
            }

            // Selective models - copy types and specific selected models
            const copyData = {
              type_ids: Array.from(selectedGlobalTypes),
              include_models: true,
              selective_models: true,
              selected_models: Object.fromEntries(
                Array.from(selectedModels.entries()).map(([typeId, modelSet]) => [
                  typeId,
                  Array.from(modelSet)
                ])
              )
            };
            await axios.post(`${API_BASE_URL}/safetyequipmenttype/copy-global-with-models/`, copyData, config);
          } else {
            // Copy all models for selected types
            const copyData = {
              type_ids: Array.from(selectedGlobalTypes),
              include_models: true
            };
            await axios.post(`${API_BASE_URL}/safetyequipmenttype/copy-global-with-models/`, copyData, config);
          }
        } else {
          // Check if user has selected any models even without copyWithModels checkbox
          const hasAnySelectedModels = selectedModels.size > 0 &&
            Array.from(selectedModels.values()).some(modelSet => modelSet.size > 0);

          if (hasAnySelectedModels) {
            // User selected specific models - copy types with selected models
            const copyData = {
              type_ids: Array.from(selectedGlobalTypes),
              include_models: true,
              selective_models: true,
              selected_models: Object.fromEntries(
                Array.from(selectedModels.entries()).map(([typeId, modelSet]) => [
                  typeId,
                  Array.from(modelSet)
                ])
              )
            };
            await axios.post(`${API_BASE_URL}/safetyequipmenttype/copy-global-with-models/`, copyData, config);
          } else {
            // Original behavior - copy only types
            const selectedTypes = globalTypes.filter(type => selectedGlobalTypes.has(type.id));

            // Create copies of selected global types
            const createPromises = selectedTypes.map(type => {
              const typeData = {
                name: type.name,
                comment: type.comment || '',
                global_type_id: type.id, // Store reference to global type
                company_specific: true
              };
              return axios.post(`${API_BASE_URL}/safetyequipmenttype/`, typeData, config);
            });

            await Promise.all(createPromises);
          }
        }
      } // Close the else block

      onSave && onSave();
      onClose();
    } catch (err) {
      console.error('Error saving type:', err);
      setError(err.response?.data?.detail || 'Failed to save type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl h-[95vh] flex" style={{ width: selectedTypeForModels ? '1420px' : '640px', transition: 'width 0.3s ease' }}>
        {/* Main Panel */}
        <div className="flex flex-col" style={{ width: '640px' }}>
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'edit' ? 'Edit Equipment Type' : 'Add Equipment Type from Global Database'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 flex-1 flex flex-col min-h-0">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex-shrink-0">
              {error}
            </div>
          )}

          {/* Info Message for new logic */}
          {mode === 'create' && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4 flex-shrink-0">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Equipment Types from Global Database Only
                  </h3>
                  <p className="mt-2 text-sm text-blue-700">
                    You can only add equipment types that exist in the global database. Select from the available global types below to create exact copies for your company.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Edit Form - only for edit mode */}
          {mode === 'edit' && activeMode === 'create' && (
            <div className="space-y-4">
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Limited Editing
                    </h3>
                    <p className="mt-2 text-sm text-amber-700">
                      You can only edit the comment. The type name cannot be changed to maintain consistency with the global database.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                  placeholder="Type name (read-only)"
                />
                <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment
                </label>
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Optional comment or description"
                />
              </div>
            </div>
          )}

          {/* Global Types Selection - only for create mode */}
          {mode === 'create' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">Global Equipment Types</h3>
                <span className="text-sm text-gray-500">
                  {selectedGlobalTypes.size} selected
                </span>
              </div>

              {/* Copy with models option */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 flex-shrink-0">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="copyWithModels"
                    checked={copyWithModels}
                    onChange={(e) => {
                      setCopyWithModels(e.target.checked);
                      if (e.target.checked) {
                        // When enabling copy all models, close model selection panel
                        setSelectedTypeForModels(null);
                        setSelectedModels(new Map());
                      } else {
                        // When disabling, also clear everything
                        setSelectedTypeForModels(null);
                        setSelectedModels(new Map());
                        setTypeModels(new Map());
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="copyWithModels"
                    className="ml-2 text-sm text-gray-900 cursor-pointer"
                    title="When enabled, all models from selected types will be copied to your company. When disabled, you can select specific models for each type."
                  >
                    <strong>Copy all associated models</strong>
                  </label>
                </div>
              </div>

              {/* Types List - Scrollable */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {fetchingGlobal ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading global types...</p>
                  </div>
                ) : globalTypes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No global equipment types available</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-md">
                    {globalTypes.map((type) => (
                      <div
                        key={type.id}
                        className={`flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer ${
                          selectedGlobalTypes.has(type.id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleGlobalTypeToggle(type.id)}
                      >
                        <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                          selectedGlobalTypes.has(type.id)
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'border-gray-300'
                        }`}>
                          {selectedGlobalTypes.has(type.id) && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{type.name}</h4>
                              {type.comment && (
                                <p className="text-sm text-gray-600 mt-1">{type.comment}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {type.model_count !== undefined && type.model_count > 0 && !copyWithModels && selectedGlobalTypes.has(type.id) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTypeForModels(type.id);
                                    fetchModelsForType(type.id);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  select models to copy
                                </button>
                              )}
                              {type.model_count !== undefined && (
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                  {type.model_count} model{type.model_count !== 1 ? 's' : ''}
                                  {!copyWithModels && selectedModels.get(type.id)?.size > 0 && (
                                    <span> ({selectedModels.get(type.id).size} selected)</span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          </div>

          {/* Footer - Fixed */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>
              {loading ? 'Saving...' :
               mode === 'edit' ? 'Update' :
               (() => {
                 let text = `Add ${selectedGlobalTypes.size} Type${selectedGlobalTypes.size !== 1 ? 's' : ''}`;
                 if (copyWithModels) {
                   const totalSelectedModels = Array.from(selectedModels.values()).reduce(
                     (sum, modelSet) => sum + modelSet.size, 0
                   );
                   if (totalSelectedModels > 0) {
                     text += ` with ${totalSelectedModels} Selected Model${totalSelectedModels !== 1 ? 's' : ''}`;
                   } else {
                     text += ' with All Models';
                   }
                 }
                 return text;
               })()}
            </span>
          </button>
          </div>
        </div>

        {/* Models Panel - Slides in from right */}
        {selectedTypeForModels && (() => {
          const type = globalTypes.find(t => t.id === selectedTypeForModels);
          const allModels = typeModels.get(selectedTypeForModels) || [];
          const filteredModels = allModels.filter(model =>
            model.name.toLowerCase().includes(modelSearch.toLowerCase()) ||
            (model.brand && model.brand.toLowerCase().includes(modelSearch.toLowerCase()))
          );
          const selectedModelsForType = selectedModels.get(selectedTypeForModels) || new Set();
          const isLoadingModels = loadingModels.has(selectedTypeForModels);

          if (!type) return null;

          return (
            <div className="border-l border-gray-200 flex flex-col bg-gray-50" style={{ width: '780px' }}>
              {/* Models Panel Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Select Models</h3>
                  <p className="text-sm text-gray-600 mt-1">{type.name}</p>
                </div>
                <button
                  onClick={() => setSelectedTypeForModels(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Search and Controls */}
              <div className="p-6 border-b border-gray-200 bg-white space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search models by name or brand..."
                    value={modelSearch}
                    onChange={(e) => setModelSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedModelsForType.size} of {filteredModels.length} models selected
                    {modelSearch && filteredModels.length !== allModels.length && (
                      <span className="text-gray-500"> (filtered from {allModels.length})</span>
                    )}
                  </span>
                  {filteredModels.length > 0 && (
                    <button
                      onClick={() => {
                        const filteredIds = filteredModels.map(m => m.id);
                        const selectedFilteredCount = filteredIds.filter(id => selectedModelsForType.has(id)).length;

                        if (selectedFilteredCount === filteredIds.length) {
                          // Deselect all filtered
                          setSelectedModels(prev => {
                            const newMap = new Map(prev);
                            const currentSet = newMap.get(selectedTypeForModels) || new Set();
                            const newSet = new Set(currentSet);
                            filteredIds.forEach(id => newSet.delete(id));
                            newMap.set(selectedTypeForModels, newSet);
                            return newMap;
                          });
                        } else {
                          // Select all filtered
                          setSelectedModels(prev => {
                            const newMap = new Map(prev);
                            const currentSet = newMap.get(selectedTypeForModels) || new Set();
                            const newSet = new Set(currentSet);
                            filteredIds.forEach(id => newSet.add(id));
                            newMap.set(selectedTypeForModels, newSet);
                            return newMap;
                          });
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {(() => {
                        const filteredIds = filteredModels.map(m => m.id);
                        const selectedFilteredCount = filteredIds.filter(id => selectedModelsForType.has(id)).length;
                        return selectedFilteredCount === filteredIds.length ? 'Deselect All' : 'Select All';
                      })()} Filtered
                    </button>
                  )}
                </div>
              </div>

              {/* Models List */}
              <div className="flex-1 p-6 overflow-y-auto">

                {isLoadingModels ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading models...</p>
                  </div>
                ) : allModels.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No models available for this type</p>
                  </div>
                ) : filteredModels.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No models found matching "{modelSearch}"</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredModels.map(model => (
                      <div
                        key={model.id}
                        className={`border rounded-lg transition-colors cursor-pointer ${
                          selectedModelsForType.has(model.id)
                            ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => handleModelToggle(selectedTypeForModels, model.id)}
                      >
                        <div className="p-4">
                          <div className="flex items-center">
                            {/* Checkbox */}
                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
                              selectedModelsForType.has(model.id)
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'border-gray-300 bg-white'
                            }`}>
                              {selectedModelsForType.has(model.id) && <Check className="w-3 h-3" />}
                            </div>

                            {/* Image */}
                            <div className="w-16 h-16 mr-3 flex-shrink-0 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                              {model.image && model.image !== '' ? (
                                <img
                                  src={model.image}
                                  alt={model.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<span class="text-xs text-gray-400 text-center px-1">No image</span>';
                                  }}
                                />
                              ) : (
                                <span className="text-xs text-gray-400 text-center px-1">No image</span>
                              )}
                            </div>

                            {/* Model info */}
                            <div className="min-w-0 flex-1">
                              <h4
                                className="text-sm font-medium text-gray-900 truncate cursor-help"
                                title={model.name}
                              >
                                {model.name}
                              </h4>
                              {model.brand && (
                                <p
                                  className="text-xs text-gray-500 truncate cursor-help"
                                  title={`Brand: ${model.brand}`}
                                >
                                  Brand: {model.brand}
                                </p>
                              )}
                            </div>

                            {/* Right section - Files grid */}
                            <div className="flex ml-4 flex-shrink-0">
                              {/* Specification column */}
                              <div className="flex flex-col items-center justify-center text-center" style={{ width: '72px' }}>
                                <div className="text-xs text-gray-600 mb-1">Specification</div>
                                <div>
                                  {model.specification_file && model.specification_file !== '' ? (
                                    <span className="text-green-600 font-medium">✓</span>
                                  ) : (
                                    <span className="text-red-500 font-medium">✗</span>
                                  )}
                                </div>
                              </div>

                              {/* Instructions column */}
                              <div className="flex flex-col items-center justify-center text-center" style={{ width: '72px' }}>
                                <div className="text-xs text-gray-600 mb-1">Instructions</div>
                                <div>
                                  {model.instructions_for_use_file && model.instructions_for_use_file !== '' ? (
                                    <span className="text-green-600 font-medium">✓</span>
                                  ) : (
                                    <span className="text-red-500 font-medium">✗</span>
                                  )}
                                </div>
                              </div>

                              {/* Declaration column */}
                              <div className="flex flex-col items-center justify-center text-center" style={{ width: '72px' }}>
                                <div className="text-xs text-gray-600 mb-1">Declaration</div>
                                <div>
                                  {model.declaration_of_conformity_file && model.declaration_of_conformity_file !== '' ? (
                                    <span className="text-green-600 font-medium">✓</span>
                                  ) : (
                                    <span className="text-red-500 font-medium">✗</span>
                                  )}
                                </div>
                              </div>

                              {/* Inspection column */}
                              <div className="flex flex-col items-center justify-center text-center" style={{ width: '72px' }}>
                                <div className="text-xs text-gray-600 mb-1">Inspection</div>
                                <div>
                                  {model.inspection_record_file && model.inspection_record_file !== '' ? (
                                    <span className="text-green-600 font-medium">✓</span>
                                  ) : (
                                    <span className="text-red-500 font-medium">✗</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Description below if exists */}
                          {model.description && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <p className="text-xs text-gray-600 line-clamp-2">{model.description}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default EquipmentTypeModal;